import * as d3 from "d3";

import * as ATCAConstants from './ATCAConstants'
import * as AstroUtil from '../astro/AstroUtil';
import * as Transform from '../astro/Transform';
import * as Planets from '../astro/Planets';

import { Validator } from 'jsonschema';

// convert kb to mb, gb, tb
export const format_kbytes = (size: number) => {
  // 2 ** 10 = 1024
  const power = 2 ** 10;
  let n = 0;
  // k => kilo, m => mega, g => giga, t => tera
  const power_labels: any = { 0: 'k', 1: 'm', 2: 'g', 3: 't' };
  let newSize = size;
  while (newSize > power) {
    newSize /= power;
    n += 1;
  }
  
  return '' + (Math.round(newSize * 100))/100 + power_labels[n] + 'b';
}

export const getZoomConfigurations = (correlatorSettings: any, name: string) => {
  for (let config of correlatorSettings) {
    if (config['name'] === name) {
      if (config['zoom'])
        return config['zoom'];
      else
        return [];
    }
  }

  return [];
}

export const getZoomBand = (correlatorSettings: any[], 
  corrName: string, zoomName: string) => {
  for (let config of correlatorSettings) {
    if (config['name'] === corrName) {
      const zooms = config['zoom'] || [];

      for (let z of zooms) {
        if (z['name'] === zoomName) {
          return z;
        }
      }
    }
  }
  return null;
}

// export const validateZoomBands = (corr_modes: any) => {
//   const zooms = corr_modes['sub_band_configuration'];
//   const validities: any[] = [];

//   for (let zoom of zooms) {
//     const validity = validateZoomBand(corr_modes, zoom);
//     validities.push(validity);
//   }
//   return validities;
// }

export const validateZoomBand = (correlatorSettings: any[],
  corrSetting: any, zoom: any): any => {
  
  // remove legacy field
  if (zoom) {
    delete zoom.message;
  }

  const freqConfig = corrSetting['frequency_configuration'] || {};

  if (!zoom || !zoom['id'] || !zoom['zoom'] || !corrSetting['correlator_setting']) {
    return {
      'status': 'valid',
      'message': ''
    };
  }

  let suggestedBand: any = {};
  if (Number(zoom['centre_frequency']) === 0) {
    return {
      'status': 'valid',
      'message': ''
    };
  } else {
    const filteredList = correlatorSettings.filter((s: any) =>{
      return s['name'] === corrSetting['correlator_setting']
    });
    suggestedBand = caluculateBand(zoom['centre_frequency'], freqConfig, filteredList[0]);

    if (suggestedBand && suggestedBand['messages']) {
      return {
        'status': 'error',
        'color': '#d32f2f',
        'message': suggestedBand['messages'].join(' ')
      };
    }
  }

  const band = getZoomBand(correlatorSettings,
    corrSetting['correlator_setting'], zoom['zoom']);

  if (!band) {
    return {
      'status': 'error',
      'color': '#d32f2f',
      'message': 'invalid zoom band for ' + corrSetting['correlator_setting']
    };
  }

  const centerFreq = Number(zoom['centre_frequency']);
  const bandWidth = band['spectral_resolution'] * band['points'] / 1000;  

  // make sure the entire zoom band is within the subband
  const zoomStartFreq = centerFreq - bandWidth/2;
  const zoomEndFreq = centerFreq + bandWidth/2;
  const sub_band_start_freq = suggestedBand['sub_band_start_freq']
  const sub_band_end_freq = suggestedBand['sub_band_end_freq']
  const subbandWidth = ATCAConstants.IF_BAND_WIDTH / ATCAConstants.SUB_BANDS_PER_IF;
  const coarse = subbandWidth * (32.0 / 27.0 - 1) /2;
  const startCoarse = sub_band_start_freq - coarse;
  const endCoarse = sub_band_end_freq + coarse;

  if ((zoomStartFreq < startCoarse) 
    || (zoomEndFreq > endCoarse)) {
    return {
      'status': 'error',
      'color': '#d32f2f',
      'message': 'Zoom outside coarse channel'
    };
  }

  if ((zoomStartFreq < sub_band_start_freq) 
    || (zoomEndFreq > sub_band_end_freq)) {
    return {
      'status': 'warning',
      'color': '#ff8f00',
      'message': 'Zoom at edge of subband'
    };


  }

  // lastly check if the zoom band overlaps with another zoomband
  const zooms = corrSetting['sub_band_configuration'];
  for (let z of zooms) {
    // skip if it's self or no zoom set
    if (z['id'] === zoom['id'] || !z['id'] || !z['zoom'])
      continue;

    if ((zoom['band'] === z['band']) && (zoom['subband'] === z['subband'])) {
      const zBand = getZoomBand(correlatorSettings,
        corrSetting['correlator_setting'], z['zoom']);
      if (!zBand) {
        return {
          'status': 'error',
          'color': '#d32f2f',
          'message': 'invalid zoom band for ' + corrSetting['correlator_setting']
        };
      }
      
      const zBandWidth = zBand['spectral_resolution'] * zBand['points'] / 1000;
      const zCentreFreq = Number(z['centre_frequency']);

      const bandOverlap = isBandsOverlap(centerFreq, bandWidth, zCentreFreq, zBandWidth);

      if (bandOverlap) {
        return ({
          'status': 'warning',
          'color': '#ff8f00',
          'message': 'zoom band overlaps with another zoom band'
        });
      }
    }
  }

  // all good
  return {
    'status': 'valid',
    'color': 'none',
    'message': ''
  };
}

export const zoomExists = (zooms: any[], band: number, subband: number, zoom: string) => {
  for (let z of (zooms||[])) {
    if ((z['band'] === band) && (z['subband'] === subband) &&
      (z['zoom'] === zoom)) {
      return true;
    }
  }
  return false;
}

export const isBandsOverlap = (centreFreq1: number, bandWidth1: number, centreFreq2: number, bandWidth2: number) => {
  const startFreq1 = centreFreq1 - bandWidth1 / 2;
  const endFreq1 = centreFreq1 + bandWidth1 / 2;

  const startFreq2 = centreFreq2 - bandWidth2 / 2;
  const endFreq2 = centreFreq2 + bandWidth2 / 2;

  if ((startFreq1 <= endFreq2) && (startFreq1 >= startFreq2)) {
    return true;
  }

  if ((endFreq1 <= endFreq2) && (endFreq1 >= startFreq2)) {
    return true;
  }

  return false;
}

export const getBandCentreFreq = (centreFreq: number, band: number, bandSetting: any={}) => {
  let freq: number|undefined = centreFreq;
  let delta_f0 = 0;

  const receiver4cm = ATCAConstants.get4cmReceiver();
  const receiver16cm = ATCAConstants.get16cmReceiver();
  if ((centreFreq >= receiver4cm['start'])
    && (centreFreq <= receiver4cm['end'])) {
      freq = receiver4cm['default_center_freq'];
  } else if ((centreFreq >= receiver16cm['start'])
    && (centreFreq <= receiver16cm['end'])) {
    return centreFreq;
  } else {
    const band1_offset = ((bandSetting||{})['band1_offset'])||0;
    const band4_offset = ((bandSetting||{})['band4_offset'])||0;

    const band1 = ATCAConstants.CABB_BAND_OFFSETS['band_1']['offset_' + band1_offset];
    const band4 = ATCAConstants.CABB_BAND_OFFSETS['band_4']['offset_' + band4_offset];

    delta_f0 = (band1 + band4)/2;
  }

  const offset = ((bandSetting||{})['band' + band + '_offset'])||0;
  const offsets = ATCAConstants.CABB_BAND_OFFSETS;
  const freqOffset = offsets['band_' + band]['offset_' + offset];
  return freq + freqOffset - delta_f0;
}

// returns freq in MHz
export const getStartFreq = (freqConfig: any, band: number, subband: number, bandSetting: any={}) => {
  if (!band || !subband) {
    return 0;
  }
  const centreFreq = Number(freqConfig['centreFreq']||'');
  const bandCentreFreq = getBandCentreFreq(
    centreFreq, band, bandSetting
  );

  const freq = bandCentreFreq - ATCAConstants.IF_BAND_WIDTH / 2
    + (subband - 1) * ATCAConstants.IF_BAND_WIDTH / ATCAConstants.SUB_BANDS_PER_IF;

  return Number(freq.toFixed(3));
};

// returns freq in MHz
export const getEndFreq = (freqConfig: any, band: number, subband: number, bandSetting: any={}) => {
  if (!band) {
    return 0;
  }
  const centreFreq = Number(freqConfig['centreFreq']||'');
  const bandCentreFreq = getBandCentreFreq(
    centreFreq, band, bandSetting
  );

  const freq = bandCentreFreq - ATCAConstants.IF_BAND_WIDTH / 2
    + subband * ATCAConstants.IF_BAND_WIDTH / ATCAConstants.SUB_BANDS_PER_IF;

  return Number(freq.toFixed(3));
};

export const getTargets = (content: string) => {
  // get rid of bad characters and coments
  let str = content.replace(/[^\x00-\x7F]/g, "");
  str = str.replace(/^[#@][^\r\n]+[\r\n]+/mg, '');

  let targets = d3.csvParseRows(str, function (d, i) {
    if (d && d.length > 0) {
      const velocity = getVelocity(d.length > 4 ? d[4].trim() : '');

      return {
        name: truncate(d[0], ATCAConstants.MAX_NAME_LENGTH),
        ra: d.length > 1 ? truncate(d[1], ATCAConstants.MAX_RA_DEC_LENGTH) : '',
        dec: d.length > 2 ? truncate(d[2], ATCAConstants.MAX_RA_DEC_LENGTH) : '',
        coord_sys: d.length > 3 ? getCoordSystem(d[3]) :'J2000',
        id: Date.now() + i,
        velocity: velocity['velocity'],
        frame: velocity['frame'],
        convention: velocity['convention']
      };
    }
  });

  return targets;
}

const truncate = (val: string, limit: number) => {
  const theValue = (val || '').trim();
  if (theValue.length > limit) {
    return theValue.substring(0, limit);
  }

  return theValue;
}

const getVelocity = (vel_string: string) => {
  const velocity = {
    'velocity': '',
    'frame': 'lsr',
    'convention': 'radio'
  };

  if (vel_string && vel_string.trim()) {
    const vels = vel_string.trim().split(' ');
    if (vels.length > 0) {
      velocity['velocity'] = Number.parseFloat(vels[0]).toFixed(2);

      if (vels.length > 1) {
        if (vels[1].trim().toLowerCase() === 'barycentric')
          velocity['frame'] = 'barycentric';
      }

      if (vels.length > 2) {
        if (vels[2].trim().toLowerCase() === 'optical')
          velocity['convention'] = 'optical';

        if (vels[2].trim().toLowerCase() === 'z')
          velocity['convention'] = 'z';
      }
    }
  }

  return velocity;
}

const getCoordSystem = (coord: string) => {
  const theCoord = (coord||'').trim();
  const matches = ATCAConstants.COORDINATE_SYSTEMS.filter(
    (val) => (val.toLowerCase() === theCoord.toLowerCase())
  );

  if (matches.length > 0)
    return matches[0];
  else
    return 'J2000';
}

export const targetToString = (target: any) => {
  const velocity = getTargetVelocityString(target);
  let content = `${target['name']}, ${target['ra']}, ${target['dec']}, ${target['coord_sys']||'J2000'},`;
  if (velocity)
    content += ' ' + velocity

  return content;
}

export const getTargetVelocityString = (target: any) => {
  let velocity = '';
  if ((target['velocity']||'').trim()) {
    velocity =
      [
        target['velocity'],
        target['frame'] ? target['frame'] : 'lsr',
        target['convention'] ? target['convention'] : 'radio'
      ].join(' ');
  }

  return velocity; 
}

export const caluculateBand = (skyFrequency: number, freqConfig: any, corrSetting: any) => {

  if (!corrSetting)
    return null;

  if (skyFrequency <= 0 || !(freqConfig||{})['band_setting'])
    return null;

  const receiverName = freqConfig['receiver'];
  const zoomFreq = Number(skyFrequency);
  const settings = ATCAConstants.BAND_SETTINGS.filter(
    (s: any) => { return (
      (s['name'] === (freqConfig||{})['band_setting'])
      && receiverName
      && (s['compatible_receivers'][receiverName])
    )}
  );

  if (settings.length <= 0)
    return null;

  const bandSetting = settings[0];

  // recommend band, subband, zoom and centre freq for sky freq
  // work out which band first
  for (let i=1; i<=corrSetting['number_of_IF']; i++) {
    for (let j=1; j<=corrSetting['subbands_per_IF']; j++) {
      const startFreq = getStartFreq(freqConfig, i, j, bandSetting);
      const endFreq = getEndFreq(freqConfig, i, j, bandSetting);

      if ((zoomFreq <= endFreq) && (zoomFreq>= startFreq)) {
        return {
          'band': i,
          'subband': j,
          'sub_band_start_freq': startFreq,
          'sub_band_end_freq': endFreq,
        };
      }
    }
  }

  return {
    'messages': [`Frequency ${zoomFreq} is outside all bands`]
  }
}

export const isTargetWithZoom = (correlatorSettings: any, 
  target: any, zoomBand: any, corrSettingName: string) => {
  const skyFrequency = target['sky_frequency'];
  const zoom = getZoomBand(correlatorSettings, zoomBand['zoom'], corrSettingName);

  if (zoom && skyFrequency > 0) {
    const bandWidth = zoom['points'] * zoom['spectral_resolution'] / 1000;
    const centreFreq = Number(zoomBand['centre_frequency']);

    if (bandWidth > 0 && centreFreq > 0) {
      const highFreq = centreFreq + bandWidth / 2;
      const lowFreq = centreFreq - bandWidth / 2;

      if (skyFrequency > highFreq || skyFrequency < lowFreq) {
        return false;
      }
    }
  }

  return true;
}

export const getRaDec = (target: any, utcDate: any) => {
  const newValue = JSON.parse(JSON.stringify(target||{}));
  const name = ((target || {})['name'] || '').toLowerCase();
  if (Planets.getPlanetNames().includes(name)) {
    const date = utcDate || new Date();
    const planDate = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));  
    const radec = Planets.planet_position(planDate, name);
    const hour = radec['ra'] * 24 / 360;
    newValue['ra'] = AstroUtil.hmsstring(hour);
    newValue['dec'] = AstroUtil.dmsstring(radec['dec']);
    return newValue;
  }

  const coord = (target || {})['coord_sys'] || 'J2000';
  if (coord.toLowerCase() !== 'j2000') {
    if (coord.toLowerCase() === 'galactic') {
      newValue.l = Number((target || {})['ra']);
      newValue.b = Number((target || {})['dec']);
    }
    return Transform.coord_convert(newValue);
  }

  return target;
}

//
// https://www.narrabri.atnf.csiro.au/observing/users_guide/html/atug.html#caobs-Commands
// instruction: 
// start or (start 1/1) => [1 ,... lastscan]
// start n => [n, ... lastscan]
// start n-m => [n, ... m] (including m)
// start n/l => [n, ... lastscan] + ([1, ... lastscan] * (l-1) )
// start n-m/l => [n, ... m] * l
export const instructionToScans = (instruction: string, length: number): number[] => {
  let scans: number[] = [];

  if (!instruction || !instruction.includes('start')) {
    return scans;
  }

  const parts = instruction.split(' ');
  if (parts.length < 2) {
    for (let i = 1; i <= length; i++) {
      scans.push(i);
    }
    return scans;
  }

  const rangeRepeatParts = parts[1].split('/');
  let rangeStr = '';
  let repeatStr = '1';

  rangeRepeatParts.length === 2 
  ? 
  [rangeStr, repeatStr] = rangeRepeatParts
  : 
  rangeStr = rangeRepeatParts[0];

  const rangeParts = rangeStr.split('-');
  let start = 1;
  let end = length;
  rangeParts.length === 2 
  ? 
  [start, end] = rangeParts.map(Number)
  : 
  start = Number(rangeParts[0]);

  const repeat = Number(repeatStr)
  for (let i = start; i <= end; i++) {
    scans.push(i);
  }
  
  if (repeat > 1) {
    let newStart = (rangeParts.length === 2 ? start : 1)

    const arr = [];
    for (let i = newStart; i <= end; i++) {
      arr.push(i);
    }

    const repeatArr = Array(repeat - 1).fill(arr).flat();
    scans = scans.concat(repeatArr);
  }

  return scans;
}

export const validateTarget = (target: any) => {
  const isValid = {
    nameHelperText: '',
    isNameValid: false,
    isValid: false,
    isPlanet: false,
    isRAValid: false,
    isDecValid: false
  }

  if (!target) {
    return isValid;
  }

  isValid['isPlanet'] = Planets.getPlanetNames().includes(target['name']);

  if (isValid['isPlanet']) {
    isValid['isRAValid'] = true;
    isValid['isDecValid'] = true;
    isValid['isNameValid'] = true;
  } else {
    isValid['isRAValid'] = validateRA(target['ra'], target['coord_sys']);
    isValid['isDecValid'] = validateDec(target['dec'], target['coord_sys']);

    const nameValidation = validateName(target['name'], target['coord_sys']);
    isValid['isNameValid'] = nameValidation['isValid'];
    isValid['nameHelperText'] = nameValidation['message'];
  }

  isValid['isValid'] = isValid['isPlanet'] ? true : isValid['isRAValid'] && isValid['isDecValid'] && isValid['isNameValid'];
  
  return isValid;
}

export const validateName = (theName: string, coord_sys: string) => {
  const coord = coord_sys || 'J2000';
  const name = (theName||'').trim();

  if (!name) {
    if ((coord.toLowerCase() === 'j2000') || (coord.toLowerCase() === 'b1950')) {
      return {
        isValid: true,
        message: 'Name will be constructed from RA Dec'
      };
    } else {
      return {
        isValid: false,
        message: 'Please enter a name'
      }
    }
  }

  if (name.length > ATCAConstants.MAX_NAME_LENGTH) {
    return {
      isValid: false,
      message: `Target name has to be less than ${ATCAConstants.MAX_NAME_LENGTH} characters`
    }
  }

  const regex = new RegExp(ATCAConstants.TARGET_NAME_REGEX);
  const match = regex.test(name);

  if (match) {
    return {
      isValid: true,
      message: ''
    }
  } else {
    return {
      isValid: false,
      message: 'Target name can only contain alphanumeric characters and .-_+'
    }
  }
}

export const validateRA = (ra: string, coord_sys: string) => {
  if (coord_sys && (coord_sys.toLowerCase() === 'galactic')) {
    const longitude = Number(ra||'.');
    if (longitude >= ATCAConstants.GALACTIC_LONGITUTE_RANGE[0] && 
        longitude <= ATCAConstants.GALACTIC_LONGITUTE_RANGE[1]) {
      return true;
    }
    return false;
  } else if (coord_sys && (coord_sys.toLowerCase() === 'azel')) {
    const az = Number(ra||'.');
    if (az >= ATCAConstants.AZ_RANGE[0] && 
        az <= ATCAConstants.AZ_RANGE[1]) {
      return true;
    }
    return false;
  }

  // match with J2000
  const match = ATCAConstants.RA_REGEX.test(ra);
  return match;
}

export const validateDec = (dec: string, coord_sys: string) => {
  if (coord_sys && (coord_sys.toLowerCase() === 'galactic')) {
    const latitude = Number(dec||'.');
    if (latitude >= ATCAConstants.GALACTIC_LATITUTE_RANGE[0] && 
        latitude <= ATCAConstants.GALACTIC_LATITUTE_RANGE[1]) {
      return true;
    }
    return false;
  } else if (coord_sys && (coord_sys.toLowerCase() === 'azel')) {
    const el = Number(dec||'.');
    if (el >= ATCAConstants.EL_RANGE[0] &&
        el <= ATCAConstants.EL_RANGE[1]) {
      return true;
    }
    return false;
  }

  // match with J2000
  const match = ATCAConstants.DEC_REGEX.test(dec);
  return match;
}

const validateRawCorrelatorConfig = (correlatorConfig: any, correlatorSettings: any) => {
  const errors = [];

  for (let config of (correlatorConfig || [])) {
    if (!config['correlator_setting']) {
      continue;
    }

    const result = validateRawFreq(config['frequency_configuration']);
    if (!result['isValid']) {
      errors.push(`Error in \'${config['name']}\': ${result['message']}`);
      continue;
    }

    const settings = correlatorSettings.filter(
      (setting: any) => 
          (setting['name'] === config['correlator_setting']) 
          && 
          (setting['compatible_receivers'][result['receiver']])
    );

    if (settings.length < 1) {
      errors.push(`Invalid correlator setting: ${config['correlator_setting']}`);
    }
    
    for (let zoom of config['sub_band_configuration']) {
      const valid = validateZoomBand(correlatorSettings, config, zoom);
      if (valid['status'] !== 'valid') {
        errors.push(`${valid['status']} in \'${config['name']}\' \'${zoom['zoom']}\': ${valid['message']}`);
      }
    }
  }
  return errors;
}

const validateScans = (schedule: any) => {
  const errors = [];
  const correlatorConfig = schedule['correlator_configurations']||[];
  const scans = (schedule['schedule']||{})['scans']||[];

  for (let scan of scans) {
    const config = scan['correlator_configuration'];
    const matches = correlatorConfig.filter(
      (c: any) => c['name'] === config);

    if (matches.length !== 1) {
      const error = `Invalid correlator configuration for scan ${scan['name']}: ${config}`;
      errors.push(error);
    }
  }
  return errors;
}

export const validateScheduleFile = (schedule: any, correlatorSettings: any) => {
  const validator = new Validator();
  const result = validator.validate(schedule, ATCAConstants.SCHEDULE_SCHEMA);

  let errors: string[] = [];
  if (result.errors) {
    for (let error of result.errors) {
      let str = error.stack;

      if (typeof error.instance !== 'object') {
        str += `: (value ${error.instance})`;
      }
      errors.push(str);
    }
  }

  // now validate the values
  // 1. validate the correlator_setting is valid
  // 2. validate each scan has correct correlator_configuration
  let correlatorErrors = validateRawCorrelatorConfig(
    schedule['correlator_configurations'], 
    correlatorSettings);
  errors.push(...correlatorErrors);
  
  let scheduleErrors = validateScans(schedule);
  errors.push(...scheduleErrors);

  return errors;
}

export const validateRawFreq = (freqConfig: any) => {
  const bandSettings = ATCAConstants.BAND_SETTINGS;
  const bandSettingName = freqConfig['band_setting'] || '';
  const centreFreq = Number(freqConfig['centreFreq'] || '')
  const offsets = freqConfig['offsets'] || null;

  let error = ''

  if (centreFreq <= 0) {
    error = 'Enter a centreFreq';
    return {
      isValid: false,
      message: error,
      receiver: ''
    };
  }

  if (!offsets && !bandSettingName) {
    return {
      isValid: false,
      message: 'Need band_setting or offsets',
      receiver: ''
    };
  }

  if (offsets && offsets.length !== 4) {
    error = 'Need four numbers for offsets';
    return {
      isValid: false,
      message: error,
      receiver: ''
    };
  }

  // find the receiver, 
  // make sure it's within the receiver range
  let receiver = null;
  for (const rec of ATCAConstants.ReceiverRange) {
    const freq_start = rec['start'];
    const freq_end = rec['end'];
    if (centreFreq >= freq_start && centreFreq <= freq_end) {
      receiver = rec;
      if (rec['name'] === '4cm') {
        return {
          isValid: true,
          message: '',
          receiver: '4cm'
        };
      }
      break;
    }
  }

  if (!receiver) {
    error = 'centreFreq out of all receiver ranges';
    return {
      isValid: false,
      message: error,
      receiver: ''
    };
  }

  const receiverName = receiver['name'];
  let bandSetting = {};

  if (!offsets) {
    const matches =  bandSettings.filter((setting) => {
      return (
        setting['name'] == bandSettingName
        && (setting['compatible_receivers'][receiverName])
      )
    });
    if (matches.length <= 0) {
      error = 'Incorrect band setting';
      return {
        isValid: false,
        message: error,
        receiver: ''
      };
    }
    bandSetting = matches[0];
  } else {
    bandSetting = {
      "band1_offset": offsets[0],
      "band2_offset": offsets[1],
      "band3_offset": offsets[2],
      "band4_offset": offsets[3],
    }
  }

  return validateIFBands(centreFreq, bandSetting, receiver);
}

export const validateFreq = (freqConfig: any) => {
  let freq: number = Number((freqConfig||{})['centreFreq'] || '');
  const receiverName = (freqConfig||{})['receiver'] || '';

  if (!receiverName) {
    return {
      isValid: false,
      message: 'Choose a receiver',
      receiver: ''
    };
  }
  // check if centre freq within range
  let error = ''

  if (freq <= 0) {
    error = 'Enter a centreFreq';
    return {
      isValid: false,
      message: error,
      receiver: ''
    };
  }

  // find the receiver, 
  // make sure it's within the receiver range
  const receivers = ATCAConstants.ReceiverRange.filter(
    (rec) => {return rec['name'] === receiverName}
  )
  if (receivers.length === 0) {
    return {
      isValid: false,
      message: 'Choose a receiver',
      receiver: ''
    };
  }

  const receiver = receivers[0];
  const center_freq_start = receiver['start'];
  const center_freq_end = receiver['end'];

  if ((freq < center_freq_start) || 
      (freq > center_freq_end)){
    return {
      isValid: false,
      message: 'Outside receiver range',
      receiver: receiver['name']
    }
  }

  const bandSettings = ATCAConstants.BAND_SETTINGS;
  const settingName = freqConfig['band_setting'];
  const matches = bandSettings.filter((setting) => {
    return ((setting['name'] === settingName) &&
    (setting['compatible_receivers'][receiverName]))
  });

  if (matches.length <= 0) {
    return {
      isValid: false,
      message: 'Choose a band setting',
      receiver: receiver['name']
    };
  }

  const bandSetting = matches[0];

    // make sure central freq is divisible by 3 for 7mm receiver
  if (receiverName === '7mm') {
    const band1_offset = ((bandSetting||{})['band1_offset'])||0;
    const band4_offset = ((bandSetting||{})['band4_offset'])||0;

    const delta_f0 = (band1_offset + band4_offset)/2;
    let if_freq = 0;

    if (freq >= ATCAConstants.LSB_OR_USB_MHZ_7mm) {
      // USB
      if_freq = freq - delta_f0 + 400;
    } else {
      // LSB
      if_freq = freq + delta_f0 - 400;
    }
    
    if (if_freq > ATCAConstants.CABB_EXCLUDEZONE_MHZ_7mm[0] 
      && if_freq < ATCAConstants.CABB_EXCLUDEZONE_MHZ_7mm[1]) {
      return {
        isValid: false,
        message: `IF freq ${if_freq} is in the exclusion range`,
        receiver: receiver['name']
      }
    }

    if ((if_freq%3) !== 0) {
      return {
        isValid: false,
        message: `Analogue IF band centre freq ${if_freq} has to be divisble by 3 for 7mm`,
        receiver: receiver['name']
      }
    }
  }

  return validateIFBands(freq, bandSetting, receiver);
}


export const validateIFBands = (centreFreq: number, bandSetting: any, receiver: any) => {

  if (!receiver) {
    return {
      isValid: false,
      message: 'Choose a receiver',
      receiver: ''
    };
  }

  if (receiver['name'] === '16cm') {
    const freqObj = ATCAConstants.Receiver16cmRange['offset_' + bandSetting['band4_offset']];

    const bandFreqStart = freqObj['center_freq_start'];
    const bandFreqEnd = freqObj['center_freq_end'];

    if ((centreFreq < bandFreqStart) || (centreFreq > bandFreqEnd)) {
      return {
        isValid: false,
        message: 'centreFreq outside range for offset',
        receiver: receiver['name']
      }
    }

    return {
      isValid: true,
      message: '',
      receiver: receiver['name']
    }    
  }

  if (receiver['name'] === '4cm') {
    return {
      isValid: true,
      message: '',
      receiver: '4cm'
    };
  }

  for (let i = 1; i <= receiver['number_of_IF']; i++) {
    const bandCentreFreq = getBandCentreFreq(centreFreq, i, bandSetting);
    const bandFreqStart = bandCentreFreq - ATCAConstants.IF_BAND_WIDTH/2;
    const bandFreqEnd = bandCentreFreq + ATCAConstants.IF_BAND_WIDTH/2;

    if ((bandFreqStart < receiver['start'])
        || (bandFreqStart > receiver['end'])
        || (bandFreqEnd < receiver['start'])
        || (bandFreqEnd > receiver['end'])
    ) {
      return {
        isValid: false,
        message: `Band ${i} spills over receiver`,
        receiver: receiver['name']
      }
    }
  }

  return {
    isValid: true,
    message: '',
    receiver: receiver['name']
  };
}

export const getDefaultCentreFreq = (receiver: any, bandSetting: string) => {
  const receiverName = (receiver||{})['name']||'';

  if (!receiver) {
    return 0;
  }

  if (receiverName !== '4cm') {
    return receiver['default_center_freq'];
  }

  const settings = ATCAConstants.BAND_SETTINGS.filter(
    (s: any) => { return (
      (s['name'] === bandSetting)
      && (s['compatible_receivers'][receiverName])
    )}
  );

  if (settings.length > 0) {
    const setting = settings[0];
    const band1CentreFreq = getBandCentreFreq(receiver['default_center_freq'], 1, setting);
    const band1StartFreq = band1CentreFreq - ATCAConstants.IF_BAND_WIDTH/2;
    const band4CentreFreq = getBandCentreFreq(receiver['default_center_freq'], 4, setting);
    const band4EndFreq = band4CentreFreq + ATCAConstants.IF_BAND_WIDTH/2;

    return (band1StartFreq + band4EndFreq)/2
  }
  return 0;
}
